import { getLeadsListQueryKey, useLeadsList, type PaginatedLeadListList } from '@gta/api-client'
import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute, stripSearchParams, useNavigate } from '@tanstack/react-router'
import { Plus, RefreshCw, Thermometer } from 'lucide-react'
import { useCallback, useMemo } from 'react'

import { DataTable } from '@/components/console/data-table'
import { PaginationBar } from '@/components/console/pagination-bar'
import { ErrorState } from '@/components/console/states'
import { PageContainer } from '@/components/layout/page-container'
import { Button } from '@/components/ui/button'
import { errorMessageOf, unwrapResponse } from '@/features/leads/api-utils'
import {
  LEAD_POOL_STATE_LABELS,
  LEAD_POOL_STATE_VALUES,
  PAGE_SIZE_OPTIONS,
  type LeadOrdering,
} from '@/features/leads/constants'
import { createLeadColumns } from '@/features/leads/lead-columns'
import { LeadFilterBar } from '@/features/leads/lead-filter-bar'
import { ALL_VIEW_SEARCH, POOL_VIEW_SEARCH, isPoolView } from '@/features/leads/lead-pool'
import {
  LEAD_LIST_STRIP_DEFAULTS,
  hasActiveLeadFilters,
  leadListSearchSchema,
  toLeadsListParams,
  type LeadListSearch,
} from '@/features/leads/search-schema'
import { useMetaEnums } from '@/features/leads/use-meta-enums'
import { useRoleAccess } from '@/lib/role-access'

/**
 * 线索列表页。
 *
 * 状态纪律:筛选 / 分页 / 排序**全部**住在 URL search params(`validateSearch` 直传 zod schema),
 * 组件内不留任何筛选态,冷加载任意 `/leads?...` 都能完整还原。
 * 列表内导航一律 `replace` —— 免得连打十次筛选就在 history 里堆十条,详情页返回时直接回到当前视图。
 * 等于默认值的参数由 stripSearchParams 摘掉(A4 全站统一),裸 /leads 就是干净首屏。
 */
export const Route = createFileRoute('/_authed/leads/')({
  validateSearch: leadListSearchSchema,
  search: {
    middlewares: [stripSearchParams(LEAD_LIST_STRIP_DEFAULTS)],
  },
  component: LeadsListPage,
})

/**
 * 视图切换条(★feat-008 W11,§6.2)。
 *
 * 「总池子」入口**零新参数** —— 它就是既有的 `?owner=none`(哨兵早在,后端零改动);
 * 池内的二分才用 additive 的 `?pool=recalled|never`。故切视图仍然只是改 URL,
 * 冷加载 `/leads?owner=none&pool=recalled` 一样能完整还原(URL-first 红线)。
 */
function LeadViewTabs({
  search,
  onChange,
}: {
  search: LeadListSearch
  onChange: (patch: Partial<LeadListSearch>) => void
}) {
  const poolView = isPoolView(search)

  const tab = (label: string, active: boolean, patch: Partial<LeadListSearch>) => (
    <button
      key={label}
      type="button"
      aria-pressed={active}
      className={
        active
          ? 'rounded-md bg-brand-subtle px-2.5 py-1 text-sm text-brand'
          : 'rounded-md px-2.5 py-1 text-sm text-console-muted hover:bg-console-hover'
      }
      onClick={() => {
        onChange(patch)
      }}
    >
      {label}
    </button>
  )

  return (
    <div className="flex flex-wrap items-center gap-1">
      {tab('全部线索', !poolView, ALL_VIEW_SEARCH)}
      {tab('总池子', poolView && search.pool === undefined, POOL_VIEW_SEARCH)}
      {LEAD_POOL_STATE_VALUES.map((state) =>
        tab(LEAD_POOL_STATE_LABELS[state], search.pool === state, { ...POOL_VIEW_SEARCH, pool: state }),
      )}
    </div>
  )
}

function LeadsListPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const rootNavigate = useNavigate()
  const queryClient = useQueryClient()
  const meta = useMetaEnums()
  const { isManager } = useRoleAccess()
  const poolView = isPoolView(search)

  const params = useMemo(() => toLeadsListParams(search), [search])
  const query = useLeadsList(params, { query: { placeholderData: keepPreviousData } })
  const pageData = unwrapResponse<PaginatedLeadListList>(query.data)

  /** 改筛选:一律把页码打回第 1 页(筛完只剩 2 页时不该停在第 7 页)。 */
  const updateFilters = useCallback(
    (patch: Partial<LeadListSearch>) => {
      void navigate({ search: (prev) => ({ ...prev, ...patch, page: 1 }), replace: true })
    },
    [navigate],
  )

  const resetFilters = useCallback(() => {
    // 排序与每页条数属于「视图偏好」,不随筛选重置一起清掉。
    void navigate({
      search: (prev) => ({ page: 1, page_size: prev.page_size, ordering: prev.ordering }),
      replace: true,
    })
  }, [navigate])

  const changePage = useCallback(
    (page: number) => {
      void navigate({ search: (prev) => ({ ...prev, page }), replace: true })
    },
    [navigate],
  )

  const changePageSize = useCallback(
    (pageSize: number) => {
      void navigate({ search: (prev) => ({ ...prev, page_size: pageSize, page: 1 }), replace: true })
    },
    [navigate],
  )

  const changeOrdering = useCallback(
    (ordering: LeadOrdering) => {
      void navigate({ search: (prev) => ({ ...prev, ordering, page: 1 }), replace: true })
    },
    [navigate],
  )

  const openLead = useCallback(
    (resourceId: string) => {
      void rootNavigate({ to: '/leads/$leadId', params: { leadId: resourceId }, search: { tab: 'activities' } })
    },
    [rootNavigate],
  )

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: getLeadsListQueryKey() })
  }, [queryClient])

  const columns = useMemo(
    () =>
      createLeadColumns({
        ordering: search.ordering,
        stageLabel: meta.stageLabel,
        productFamilyLabel: meta.productFamilyLabel,
        onOrderingChange: changeOrdering,
        poolView,
        canDispatch: isManager,
        meta,
      }),
    [search.ordering, meta, poolView, isManager, changeOrdering],
  )

  const rows = pageData?.results ?? []
  const total = pageData?.count ?? 0
  const filtersActive = hasActiveLeadFilters(search)

  return (
    <PageContainer
      title="线索管理"
      breadcrumbs={[{ label: '线索管理' }]}
      description="客户咨询建档与阶段推进:筛选、分页、排序全部记在地址栏,链接可直接分享。"
      actions={
        <>
          {/* 待跟进(冷候选)是主管的收回工作台,故与收回动作同挂主管档;
              sales 看不到入口,手敲 /leads/cold 也会被路由闸挡回列表(后端同挂 403)。 */}
          {isManager && (
            <Button variant="outline" asChild>
              <Link to="/leads/cold">
                <Thermometer />
                待跟进
              </Link>
            </Button>
          )}
          <Button variant="outline" disabled={query.isFetching} onClick={refresh}>
            <RefreshCw className={query.isFetching ? 'animate-spin' : undefined} />
            刷新
          </Button>
          <Button asChild>
            <Link to="/leads/new">
              <Plus />
              新建线索
            </Link>
          </Button>
        </>
      }
    >
      <LeadViewTabs search={search} onChange={updateFilters} />

      <LeadFilterBar
        search={search}
        meta={meta}
        onChange={updateFilters}
        onReset={resetFilters}
        canReset={filtersActive}
      />

      {query.isError ? (
        <ErrorState
          title="线索列表加载失败"
          message={errorMessageOf(query.error, '请稍后重试')}
          onRetry={() => {
            void query.refetch()
          }}
        />
      ) : (
        <div className="console-card overflow-hidden" aria-busy={query.isFetching}>
          <DataTable
            columns={columns}
            data={rows}
            isLoading={query.isLoading}
            isRefreshing={query.isFetching}
            getRowId={(row) => row.resource_id}
            onRowClick={(row) => {
              openLead(row.resource_id)
            }}
            skeletonRows={Math.min(search.page_size, 8)}
            emptyTitle={poolView ? '总池子里没有线索' : '没有符合条件的线索'}
            emptyDescription={
              poolView
                ? '池 = 无归属的线索。空池是好事:所有线索都在人手里。'
                : filtersActive
                  ? '试着放宽筛选条件,或点「重置」回到全部线索。'
                  : '当前库里还没有线索记录。'
            }
          />

          <PaginationBar
            page={search.page}
            pageSize={search.page_size}
            total={total}
            isLoading={query.isLoading}
            onPageChange={changePage}
            onPageSizeChange={changePageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
          />
        </div>
      )}
    </PageContainer>
  )
}
